import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bell, Quote, Calendar, CheckSquare, Sparkles, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const CATEGORY_ICONS = {
  quote: Quote,
  schedule: Calendar,
  reminder: AlertCircle,
  checklist: CheckSquare,
  update: Sparkles,
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const storageKey = `weddly.read_notifications.${user?.user_id || "guest"}`;

  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState(() => {
    try {
      const saved = localStorage.getItem(`weddly.read_notifications.${user?.user_id || "guest"}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        setReadIds(new Set(JSON.parse(saved)));
      }
    } catch (_) {}
  }, [storageKey]);

  const saveReadIds = (newSet) => {
    setReadIds(newSet);
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(newSet)));
    } catch (_) {}
  };

  useEffect(() => {
    let unmounted = false;
    api.get("/header-notifications")
      .then((res) => {
        if (!unmounted && res?.data?.notifications) {
          setNotifications(res.data.notifications);
        }
      })
      .catch(() => {
        // Fallback default notifikasi jika offline / error
        if (!unmounted) {
          setNotifications([
            {
              id: "notif_default_quote",
              category: "quote",
              title: "Kutipan Hari Ini",
              message: '"Pernikahan yang bahagia adalah percakapan panjang yang terasa terlalu singkat."',
              time: "Hari ini",
              type: "info",
            },
            {
              id: "notif_default_update",
              category: "update",
              title: "✨ Fitur Baru Weddly",
              message: "Notifikasi pintar & pemantauan harga emas mas kawin kini hadir di header Anda!",
              time: "Terbaru",
              type: "update",
            }
          ]);
        }
      });
    return () => {
      unmounted = true;
    };
  }, []);

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  const markAllRead = () => {
    const allIds = new Set(notifications.map((n) => n.id));
    saveReadIds(allIds);
  };

  const handleItemClick = (item) => {
    const nextSet = new Set([...readIds, item.id]);
    saveReadIds(nextSet);
    if (item.link) {
      setIsOpen(false);
      navigate(item.link);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell-btn"
          aria-label="Pusat Notifikasi"
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border/60 bg-card/80 hover:bg-accent text-foreground transition-all duration-150 cursor-pointer shadow-sm select-none"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground animate-in zoom-in-50">
              {unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 md:w-96 p-0 rounded-2xl shadow-xl border border-border bg-card/95 backdrop-blur-xl overflow-hidden">
        {/* Header Dropdown */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-muted/30">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold">Notifikasi &amp; Pengingat</h4>
            {unreadCount > 0 && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {unreadCount} baru
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground"
            >
              Tandai dibaca
            </Button>
          )}
        </div>

        {/* List Notifikasi */}
        <div className="max-h-[360px] overflow-y-auto divide-y divide-border/40">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              Tidak ada notifikasi saat ini
            </div>
          ) : (
            notifications.map((item) => {
              const IconComponent = CATEGORY_ICONS[item.category] || Bell;
              const isUnread = !readIds.has(item.id);

              return (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`flex items-start gap-3 p-3.5 transition-colors duration-150 cursor-pointer ${
                    isUnread ? "bg-primary/[0.04] hover:bg-primary/[0.08]" : "hover:bg-accent/50"
                  }`}
                >
                  <div
                    className={`p-2 rounded-xl shrink-0 mt-0.5 ${
                      item.type === "warning"
                        ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                        : item.type === "success"
                        ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        : "bg-primary/15 text-primary"
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-0.5">
                      <span className="text-xs font-semibold text-foreground truncate">{item.title}</span>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{item.time}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{item.message}</p>
                    {item.link && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary mt-1 hover:underline">
                        Lihat Rincian &rarr;
                      </span>
                    )}
                  </div>

                  {isUnread && (
                    <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-2" />
                  )}
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
